import { useState } from 'react';
import styled, { css, keyframes } from 'styled-components';
import { Modal } from '@shared/ui/Modal';
import { Badge } from '@shared/ui/Badge';
import { Loader } from '@shared/ui/Progress';
import { Button } from '@shared/ui/Button';
import { IconRefresh } from '@shared/ui/icons';
import {
  apiErrorText,
  useGetCounterpartyDetailsQuery,
  useRefreshCounterpartyEgrulMutation,
  useUpdateCounterpartyNotesMutation,
} from '@shared/api';
import { counterpartyStatusLabel, counterpartyStatusTone } from '@shared/lib/statusLabels';
import { ExternalRegistries } from './ExternalRegistries';
import { RiskMap } from './RiskMap';
import { useAgentPermission } from '@entities/user';

const Grid = styled.dl`
  display: grid;
  grid-template-columns: 200px 1fr;
  gap: ${({ theme }) => theme.spacing(2)} ${({ theme }) => theme.spacing(4)};
  margin: 0;
  font-size: 14px;
  & > dt {
    color: ${({ theme }) => theme.colors.text.secondary};
  }
  & > dd {
    margin: 0;
    color: ${({ theme }) => theme.colors.text.primary};
  }
`;

const Section = styled.div`
  margin-top: ${({ theme }) => theme.spacing(5)};
  & > h4 {
    margin: 0 0 ${({ theme }) => theme.spacing(2)};
    font-size: 13px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: ${({ theme }) => theme.colors.text.secondary};
  }
`;

const DangerBanner = styled.div`
  margin-top: ${({ theme }) => theme.spacing(4)};
  padding: ${({ theme }) => theme.spacing(3)};
  background: ${({ theme }) => theme.colors.status.danger}22;
  border: 1px solid ${({ theme }) => theme.colors.status.danger}66;
  border-left: 3px solid ${({ theme }) => theme.colors.status.danger};
  border-radius: ${({ theme }) => theme.radius.md};
  color: ${({ theme }) => theme.colors.status.danger};
  font-size: 13px;
  line-height: 1.5;
`;

const WarnBanner = styled.div`
  margin-top: ${({ theme }) => theme.spacing(4)};
  padding: ${({ theme }) => theme.spacing(3)};
  background: ${({ theme }) => theme.colors.status.warning}22;
  border: 1px solid ${({ theme }) => theme.colors.status.warning}66;
  border-left: 3px solid ${({ theme }) => theme.colors.status.warning};
  border-radius: ${({ theme }) => theme.radius.md};
  color: ${({ theme }) => theme.colors.status.warning};
  font-size: 13px;
  line-height: 1.5;
`;

const Stats = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing(4)};
  margin-top: ${({ theme }) => theme.spacing(3)};
`;

const Stat = styled.div`
  flex: 1;
  padding: ${({ theme }) => theme.spacing(3)};
  background: ${({ theme }) => theme.colors.backgroundElevated};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  text-align: center;

  & > .num {
    font-size: 24px;
    font-weight: 700;
    color: ${({ theme }) => theme.colors.text.primary};
  }
  & > .lab {
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: ${({ theme }) => theme.colors.text.muted};
    margin-top: 4px;
  }
`;

const Mono = styled.span`
  font-family: ${({ theme }) => theme.font.mono};
`;

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

const RefreshIconWrap = styled.span<{ $spinning: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  ${({ $spinning }) =>
    $spinning &&
    css`
      animation: ${spin} 900ms linear infinite;
    `}
`;

const NotesArea = styled.textarea`
  width: 100%;
  min-height: 80px;
  padding: ${({ theme }) => theme.spacing(2)};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.background};
  color: ${({ theme }) => theme.colors.text.primary};
  font: inherit;
  font-size: 13px;
  line-height: 1.45;
  resize: vertical;
`;

interface Props {
  counterpartyId: string;
  onClose: () => void;
}

function fmtDate(s: string | null): string {
  if (!s) return '—';
  return new Date(s).toLocaleDateString('ru-RU');
}

export function CounterpartyCard({ counterpartyId, onClose }: Props) {
  const { can_write } = useAgentPermission('statut');
  const { data, isLoading } = useGetCounterpartyDetailsQuery(counterpartyId);
  const [refreshEgrul, { isLoading: isRefreshing }] = useRefreshCounterpartyEgrulMutation();
  const [updateNotes, { isLoading: isSavingNotes }] = useUpdateCounterpartyNotesMutation();

  const [notesDraft, setNotesDraft] = useState<string | null>(null);
  const [refreshError, setRefreshError] = useState<string | null>(null);

  if (isLoading || !data) {
    return (
      <Modal open title="Карточка контрагента" onClose={onClose}>
        <Loader />
      </Modal>
    );
  }

  const c = data.counterparty;
  const isLiquidated = c.status === 'LIQUIDATED' || c.status === 'BANKRUPT';
  const isAtRisk = c.status === 'LIQUIDATING' || c.status === 'REORGANIZING';
  const isUnknown = c.status === 'UNKNOWN' || !c.last_checked_at;

  async function onRefresh() {
    setRefreshError(null);
    try {
      await refreshEgrul(counterpartyId).unwrap();
    } catch (e) {
      setRefreshError(apiErrorText(e, 'Не удалось обновить из ЕГРЮЛ'));
    }
  }

  const currentNotes = notesDraft ?? c.notes ?? '';
  const notesChanged = notesDraft !== null && notesDraft !== (c.notes ?? '');

  async function onSaveNotes() {
    await updateNotes({
      id: counterpartyId,
      notes: (notesDraft ?? '').trim() || null,
    }).unwrap();
    setNotesDraft(null);
  }

  return (
    <Modal open title={`Контрагент · ${c.short_name ?? c.full_name}`} onClose={onClose}>
      {isLiquidated && (
        <DangerBanner>
          <strong>⚠️ Контрагент {counterpartyStatusLabel(c.status).toLowerCase()}.</strong>{' '}
          Заключение новых договоров с ним крайне рискованно. Если есть действующие договоры —
          рассмотрите досрочное расторжение.
          {c.liquidation_date && (
            <>
              {' '}
              Дата прекращения деятельности: <strong>{fmtDate(c.liquidation_date)}</strong>.
            </>
          )}
        </DangerBanner>
      )}
      {isAtRisk && (
        <WarnBanner>
          <strong>⚠️ Контрагент {counterpartyStatusLabel(c.status).toLowerCase()}.</strong> Перед
          заключением новых договоров уточните статус процедуры и полномочия подписанта.
        </WarnBanner>
      )}
      {isUnknown && data.egrul_enabled && (
        <WarnBanner>
          Контрагент ещё не сверялся с ЕГРЮЛ. Рекомендуем обновить данные — кнопка справа.
        </WarnBanner>
      )}

      <div style={{ display: 'flex', gap: 8, marginTop: 16, flexWrap: 'wrap' }}>
        {data.egrul_enabled && (
          <Button
            type="button"
            onClick={onRefresh}
            disabled={isRefreshing || !can_write}
            title={!can_write ? 'Только чтение' : undefined}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}
          >
            <RefreshIconWrap $spinning={isRefreshing}>
              <IconRefresh size={16} />
            </RefreshIconWrap>
            {isRefreshing ? 'Обновляем из ЕГРЮЛ…' : 'Обновить из ЕГРЮЛ'}
          </Button>
        )}
        {refreshError && (
          <span
            style={{
              color: 'var(--osnova-danger, #d04848)',
              fontSize: 13,
              alignSelf: 'center',
            }}
          >
            {refreshError}
          </span>
        )}
      </div>

      <Section>
        <h4>Реквизиты</h4>
        <Grid>
          <dt>ИНН</dt>
          <dd>
            <Mono>{c.inn}</Mono>
          </dd>
          {c.kpp && (
            <>
              <dt>КПП</dt>
              <dd>
                <Mono>{c.kpp}</Mono>
              </dd>
            </>
          )}
          {c.ogrn && (
            <>
              <dt>ОГРН</dt>
              <dd>
                <Mono>{c.ogrn}</Mono>
              </dd>
            </>
          )}
          <dt>Полное наименование</dt>
          <dd>{c.full_name}</dd>
          {c.short_name && c.short_name !== c.full_name && (
            <>
              <dt>Краткое</dt>
              <dd>{c.short_name}</dd>
            </>
          )}
          <dt>ОПФ</dt>
          <dd>{c.legal_form ?? '—'}</dd>
          <dt>Адрес</dt>
          <dd>{c.address ?? '—'}</dd>
          <dt>Руководитель</dt>
          <dd>
            {c.director_name ? (
              <>
                {c.director_name}
                {c.director_position && (
                  <span
                    style={{
                      color: 'var(--osnova-text-muted, #888)',
                      marginLeft: 8,
                      fontSize: 12,
                    }}
                  >
                    · {c.director_position}
                  </span>
                )}
              </>
            ) : (
              '—'
            )}
          </dd>
          <dt>Дата регистрации</dt>
          <dd>{fmtDate(c.registration_date)}</dd>
          {c.liquidation_date && (
            <>
              <dt>Дата ликвидации</dt>
              <dd>{fmtDate(c.liquidation_date)}</dd>
            </>
          )}
          <dt>Статус</dt>
          <dd>
            <Badge $tone={counterpartyStatusTone(c.status)}>
              {counterpartyStatusLabel(c.status)}
            </Badge>
          </dd>
          <dt>Последняя проверка</dt>
          <dd>
            {c.last_checked_at
              ? new Date(c.last_checked_at).toLocaleString('ru-RU')
              : 'не проверялся'}
          </dd>
        </Grid>
      </Section>

      <Section>
        <h4>Активность</h4>
        <Stats>
          <Stat>
            <div className="num">{data.contracts_count}</div>
            <div className="lab">Договоров</div>
          </Stat>
          <Stat>
            <div className="num">{data.claims_count}</div>
            <div className="lab">Претензий / исков</div>
          </Stat>
        </Stats>
      </Section>

      <Section>
        <h4>Внешние реестры</h4>
        <ExternalRegistries counterparty={c} />
      </Section>

      {data.contracts_count > 0 && (
        <Section>
          <h4>Карта рисков по контрагенту</h4>
          <RiskMap counterpartyId={c.id} />
        </Section>
      )}

      <Section>
        <h4>Заметки юриста</h4>
        <NotesArea
          value={currentNotes}
          onChange={(e) => setNotesDraft(e.target.value)}
          placeholder="Внутренние заметки по контрагенту: контакты, особенности работы, история."
        />
        {notesChanged && (
          <div style={{ marginTop: 8, display: 'flex', gap: 8 }}>
            <Button
              type="button"
              $size="sm"
              onClick={onSaveNotes}
              disabled={isSavingNotes || !can_write}
              title={!can_write ? 'Только чтение' : undefined}
            >
              {isSavingNotes ? 'Сохраняем…' : 'Сохранить заметки'}
            </Button>
            <Button
              type="button"
              $size="sm"
              $variant="secondary"
              onClick={() => setNotesDraft(null)}
            >
              Отменить
            </Button>
          </div>
        )}
      </Section>
    </Modal>
  );
}
